// Angular Imports
import { Injectable } from '@angular/core'
// Shared Imports
import { ERequestsResponseStatus, ETimeZone } from '@shared/models'

@Injectable({
	providedIn: 'root',
})
export class ToolsService {
	constructor() {}

	convertToBlob({ binaryData, contentType }: ConvertToBlob): Blob {
		return new Blob([binaryData], { type: contentType })
	}

	downloadFile({ filename, binaryData }: DownloadFile) {
		let contentType = ''
		const format = filename.split('.').at(-1)
		switch (format) {
			case 'xlsx':
				contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				break
		}

		const blob = this.convertToBlob({ binaryData, contentType })
		const downloadUrl = URL.createObjectURL(blob)
		const downloadLink = document.createElement('a')
		downloadLink.href = downloadUrl
		downloadLink.download = filename
		downloadLink.click()
		URL.revokeObjectURL(downloadUrl)
	}

	// This function, generateNumbers, takes in an object of type IGenerateNumbers as an argument and returns an array of numbers based on the provided starting point, step value, and maximum value.
	generateNumbers({ start, step, maximun }: IGenerateNumbers): number[] {
		const numbers: number[] = []
		const stepValidated = step ? step : 1
		const startValidated = start ? start : stepValidated

		for (let index = startValidated; index <= maximun; index += stepValidated) {
			numbers.push(index)
		}

		return numbers
	}

	/**
	 * Capitalizes only the first letter of a sentence
	 *
	 * @param {string | null | undefined} text - The text to transform
	 * @returns {string} The text with only the first letter capitalized
	 */
	capitalizeSentence(text: string | null | undefined): string {
		if (!text) return ''
		return text.charAt(0).toUpperCase() + text.slice(1)
	}

	/**
	 * Capitalizes the first letter of each word in a phrase
	 *
	 * @param {string} phrase - The phrase to transform
	 * @returns {string} The phrase with the first letters of each word capitalized
	 */
	capitalizeLetters(phrase: string): string {
		// This function capitalizeLetters, takes in a string as an argument and returns the same string with the first letter of each word capitalized.
		const words = phrase.split(' ')
		const wordCapitalized = words.map((word: string) => {
			const firstLetter = word.charAt(0).toUpperCase()
			const othersLetters = word.slice(1).toLowerCase()
			return firstLetter + othersLetters
		})
		return wordCapitalized.join(' ')
	}

	// Verify if value is number
	verifyIfValueIsNumber(value: any): boolean {
		return typeof value === 'number' ? true : false
	}

	/**
	 * Retrieves the date from an ISO date string
	 *
	 * @param {Object} options - The options for retrieving the date
	 * @param {string} options.dateString - The ISO date string from which to retrieve the date
	 * @param {ETimeZone} [options.timeZone] - The time zone to use for converting the date
	 * @returns {string | false} - The date in "dd/mm/yyyy" format, or `false` if the date is invalid
	 */
	getDateFromISODate(options: {
		dateAsString: string
		timeZone?: ETimeZone
		format?: 'dd:mm:yyyy'
	}): string | false {
		const { dateAsString, timeZone, format } = options

		const date = new Date(dateAsString)
		// If data is invalid return false
		if (!date?.getTime()) {
			return false
		}

		function formatDate(date: Date, format: string | undefined): string {
			const day = date.getDate().toString().padStart(2, '0')
			const month = (date.getMonth() + 1).toString().padStart(2, '0')
			const year = date.getFullYear().toString()

			switch (format) {
				case 'dd:mm:yyyy':
					return `${day}/${month}/${year}`
				default:
					return `${month}/${day}/${year}`
			}
		}

		// Has time zone
		if (timeZone && timeZone) {
			const dateByTimeZoneAsString = date.toLocaleString('es-ES', { timeZone })
			const dateByTimeZone = new Date(dateByTimeZoneAsString)

			// If data is invalid return false
			if (!dateByTimeZone?.getTime()) {
				return false
			}

			const dateFormated = formatDate(dateByTimeZone, format)
			return dateFormated ? dateFormated : false
		}

		// Default
		const dateFormated = formatDate(date, format)
		return dateFormated ? dateFormated : false
	}

	/**
	 * Retrieves the time from an ISO date string
	 *
	 * @param {Object} options - The options for retrieving the time
	 * @param {string} options.dateString - The ISO date string from which to retrieve the time
	 * @param {ETimeZone} [options.timeZone] - The time zone to use for converting the date
	 * @returns {string | false} - The time in "hh:mm" format, or `false` if the date is invalid
	 */
	getTimeFromISODate(options: { dateAsString: string; timeZone?: ETimeZone }): string | false {
		const { dateAsString, timeZone } = options

		// Has time zone
		if (timeZone) {
			const date = new Date(dateAsString)
			// If data is invalid return false
			if (!date?.getTime()) {
				return false
			}

			const dateByTimeZoneAsString = date.toLocaleString('es-ES', { timeZone })
			const dateByTimeZone = new Date(dateByTimeZoneAsString)
			const hours = dateByTimeZone?.getHours()?.toString()?.padStart(2, '0')
			const minutes = dateByTimeZone?.getMinutes()?.toString()?.padStart(2, '0')
			const time = `${hours}:${minutes}`
			return dateByTimeZone?.getTime() ? time : false
		}

		// Default
		const timeString = dateAsString.split('T')[1]
		const timeStringWithoutSeconds = timeString?.slice(0, 5)
		return timeStringWithoutSeconds ? timeStringWithoutSeconds : false
	}

	/**
	 * Gets the request status based on the provided status.
	 * @param {string} status - The status of the request.
	 * @returns {ERequestsResponseStatus} The corresponding request status.
	 */
	getStatus = (status: string | undefined): ERequestsResponseStatus => {
		const statusAsString = typeof status === 'string' ? status : ''

		switch (statusAsString) {
			case ERequestsResponseStatus.success:
				return ERequestsResponseStatus.success
			case ERequestsResponseStatus.warning:
				return ERequestsResponseStatus.warning
			case ERequestsResponseStatus.error:
				return ERequestsResponseStatus.error
			default:
				return ERequestsResponseStatus.unknown
		}
	}
}

interface ConvertToBlob {
	binaryData: ArrayBuffer | string
	contentType: string
}

interface DownloadFile {
	filename: string
	binaryData: ArrayBuffer | string
}

interface IGenerateNumbers {
	start?: number
	step?: number
	maximun: number
}
